import React from 'react';
import { useState, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, IconButton, ToggleButtonGroup, ToggleButton, Tooltip, Divider, SelectChangeEvent } from '@mui/material';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { SimpleFormLabel } from 'components/dialogs/SimpleFormDialog';
import { useTranslation } from 'react-i18next';

interface RelativeDateProps {
  disabled?: boolean;
  relativeDate: string, // "(+||-)P1D2W3M4YT5H6M7S(F||''||C)" - ISO8601 Relative Date String
  defaultRelativeDate?: string,
  useSimpifiedRelativeDates?: boolean,
  setRelativeDate: (relativeDate: string) => void,
}

interface RelativeDatePart {
  amount: string;
  unit: string;
}

interface RelativeDateUnits {
  value: string;
  label: string;
}

interface StringKeyObject {
  [key: string]: string;
}

function formatPart(relativeDateParts: RelativeDatePart[], units: string[]) {
  return relativeDateParts
    .filter(part => units.includes(part.unit))
    .map(part => `${part.amount}${part.unit[0].toUpperCase()}`)
    .join("");
}

function processPart(part: string, unitMapping: StringKeyObject, resultArray: RelativeDatePart[]) {
  const amount = part.match(/\d+/g)?.[0] || "0";
  const unit = part[part.length - 1];

  if (unitMapping[unit]) {
    resultArray.push({ amount: amount, unit: unitMapping[unit] });
  }
}

function getAvailableUnits(relativeDateParts: RelativeDatePart[], relativeDateUnits: RelativeDateUnits[]) {
  const usedUnits = relativeDateParts.map(part => part.unit);
  return relativeDateUnits.filter(unit => !usedUnits.includes(unit.value));
}

export default function RelativeDateSelector(props: RelativeDateProps) {
  const [relativeDate, setRelativeDate] = useState<string>(props.relativeDate);
  const [relativeDateParts, setRelativeDateParts] = useState<RelativeDatePart[]>([]);
  const [prefix, setPrefix] = useState<string>("-");
  const [postfix, setPostfix] = useState<string>("");
  const i18n  = useTranslation();

  const relativeDateUnits:RelativeDateUnits[] = [
    {value: "seconds", label: "Seconds"},
    {value: "minutes", label: "Minutes"},
    {value: "hours", label: "Hours"},
    {value: "days", label: "Days"},
    {value: "weeks", label: "Weeks"},
    {value: "months", label: "Months"},
    {value: "years", label: "Years"},
  ];

  /**
   * Formats the relative date parts into a relative date string.
   * @param {RelativeDatePart[]} relativeDateParts - The parts of the relative date.
   * - Formats the date and time parts of the relative date.
   * - Constructs the relative date string with the formatted date and time parts.
   * - Sets the prefix and postfix of the relative date string.
   * - Constructs the final relative date string with the prefix and postfix.
   * - Sets the relative date string state.
   */
  const formatRelativeDate = (relativeDateParts: RelativeDatePart[]) => {
    const formattedDate = formatPart(relativeDateParts, ["days", "weeks", "months", "years"]);
    const formattedTime = formatPart(relativeDateParts, ["hours", "minutes", "seconds"]);  

    const formattedDateStr = formattedDate.length > 0 ? `${formattedDate}` : "";
    const formattedTimeStr = formattedTime.length > 0 ? `T${formattedTime}` : "";

    const prefixStr = prefix.length > 0 ? `${prefix}` : "";
    const postfixStr = postfix.length > 0 ? `${postfix}` : "";

    // P should always be present in the relative date string
    const relativeDateStr = `${prefixStr}P${formattedDateStr}${formattedTimeStr}${postfixStr}`;

    setRelativeDate(relativeDateStr);
    props.setRelativeDate(relativeDateStr);
  };

  /**
   * Deconstructs the relative date string into its parts.
   * @param {string} relativeDate - The relative date string to deconstruct.
   * - Finds the prefix and postfix from the relative date string.
   * - Sets the prefix and postfix state from the relative date string.
   */
  const deconstructRelativeDate = (relativeDate: string) => {
    const firstChar = relativeDate[0];
    const lastChar = relativeDate[relativeDate.length - 1];

    firstChar === "-" ? setPrefix("-") : setPrefix("");
    lastChar === "F" ? setPostfix("F") : lastChar === "C" ? setPostfix("C") : setPostfix("");

    const newDatePartsArr: RelativeDatePart[] = [];
    const newDateParts = relativeDate.split(/P|T/);

    newDateParts.shift();

    const datePart = newDateParts[0];
    const timePart = newDateParts[1];

    // Match all instances of a number(s) followed by a letter (e.g. 1D, 12W, 3M, 4Y, 5H, 16M, 7S)
    if (datePart) {
      const unitMapping:StringKeyObject = { "D": "days", "W": "weeks", "M": "months", "Y": "years" };
      datePart.match(/\d+[A-Za-z]/g)?.forEach((part) => processPart(part, unitMapping, newDatePartsArr));
    }
    if (timePart) {
      const unitMapping:StringKeyObject = { "H": "hours", "M": "minutes", "S": "seconds" };
      timePart.match(/\d+[A-Za-z]/g)?.forEach((part) => processPart(part, unitMapping, newDatePartsArr));
    }

    setRelativeDateParts(newDatePartsArr);
  };

  const handleAddInput = () => {
    const newRelativeDateParts = [...relativeDateParts];
    newRelativeDateParts.push({amount: "", unit: ""});
    setRelativeDateParts(newRelativeDateParts);
  };

  const handleRemoveInput = (index:number) => {
    const newRelativeDateParts = [...relativeDateParts];
    newRelativeDateParts.splice(index, 1);
    setRelativeDateParts(newRelativeDateParts);
  };

  const handleAmountChange = (event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index:number) => {
    const newRelativeDateParts = [...relativeDateParts];
    newRelativeDateParts[index].amount = event.target.value;
    setRelativeDateParts(newRelativeDateParts);
  };

  const handleUnitChange = (event:SelectChangeEvent<string>, index:number) => {
    const newRelativeDateParts = [...relativeDateParts];
    newRelativeDateParts[index].unit = event.target.value;
    setRelativeDateParts(newRelativeDateParts);
  };

  // Set the initial contents of the form when the component is first rendered
  useEffect(() => {
    if (!relativeDate || relativeDate.length === 0) {
      if (props.defaultRelativeDate) {
        deconstructRelativeDate(props.defaultRelativeDate);
      } else {
        setRelativeDateParts([{amount: "3", unit: "months"}]);
      }
    } else {
      deconstructRelativeDate(relativeDate);
    }
  },[]);

  // Update the relative date string when the state of the form changes
  useEffect(() => {
    if (relativeDateParts.length !== 0) { formatRelativeDate(relativeDateParts) };
  },[relativeDateParts, relativeDateParts.length, prefix, postfix]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      {!props.useSimpifiedRelativeDates &&
        <>
        {/* Prefix Toggle */}
        <ToggleButtonGroup
          disabled={props.disabled ?? false}
          exclusive
          sx={{ marginRight: "20px" }}
          size="small"
          value={prefix}
          onChange={(event, newPrefix) => { setPrefix(newPrefix) }}>
          <ToggleButton value="-">{i18n.t('query-form.past')}</ToggleButton>
          <ToggleButton value="">{i18n.t('query-form.future')}</ToggleButton>
        </ToggleButtonGroup>

        {/* Postfix Toggle */}
        <ToggleButtonGroup
          disabled={props.disabled ?? false}
          exclusive
          sx={{ marginRight: "10px" }}
          size="small"
          value={postfix}
          onChange={(event, newPostfix) => { setPostfix(newPostfix || "") }}>
          <ToggleButton value="F">
            <Tooltip title={i18n.t("query-form.floor-tooltip")}>
              <span>{i18n.t("query-form.floor")}</span>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="">{i18n.t("none")}</ToggleButton>
          <ToggleButton value="C">
            <Tooltip title={i18n.t("query-form.ceiling-tooltip")}>
              <span>{i18n.t("query-form.ceiling")}</span>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        </>
      }
        {/* Add Input */}
      {(!props.useSimpifiedRelativeDates || !relativeDateParts.length) &&
        <Tooltip title="Add New Input">
          <IconButton disabled={props.disabled ?? false} onClick={handleAddInput}>
            <AddCircle />
          </IconButton>
        </Tooltip>
      }
      </Box>
      <Divider sx={{ margin: "10px 0" }} />

      {/* Input Fields */}
      {relativeDateParts.map((part, index) => {
        const availableUnits = getAvailableUnits(relativeDateParts.slice(0, index), relativeDateUnits);
        return (
          <Box key={index} sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", width: "90%", marginRight: "10px" }}>
              <Box sx={{ width: "33%", marginRight: "20px" }}>
                {/* Amount */}
                <SimpleFormLabel>{i18n.t("query-form.amount")}</SimpleFormLabel>
                <TextField
                  autoComplete="off"
                  disabled={props.disabled ?? false}
                  type="text"
                  value={part.amount}
                  onChange={(event) => handleAmountChange(event, index)}/>
              </Box>
              <Box sx={{ width: "66%" }}>
                {/* Unit */}
                <SimpleFormLabel>{i18n.t("query-form.unit")}</SimpleFormLabel>
                <Select
                  disabled={props.disabled ?? false}
                  sx={{ width: "100%" }}
                  value={part.unit}
                  onChange={(event) => handleUnitChange(event, index) }>
                  {availableUnits.map((unit) => {
                    return (
                      <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
                    );
                  })}
                </Select>
              </Box>
            </Box>
            <Box sx={{ display: "flex", width: "10%" }}>
              {/* Remove Input */}
              <Box sx={{marginTop: (props.useSimpifiedRelativeDates ? "30px" : "40px")}}>
              <Tooltip title="Remove Input">
                <IconButton disabled={props.disabled ?? false} onClick={() => { handleRemoveInput(index) }}>
                  <RemoveCircle />
                </IconButton>
              </Tooltip>
              {props.useSimpifiedRelativeDates &&
                <Box sx={{marginTop:"-12px"}}>
                <Tooltip title="Add New Input">
                  <IconButton disabled={props.disabled ?? false} onClick={handleAddInput}>
                    <AddCircle />
                  </IconButton>
                </Tooltip>
                </Box>
              }
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );  
}
